import "@app/App.css";
import { initialNewRowSelection } from "@app/grid/editSession";
import { CellPosition, GridCell, NEW_ROW_ID } from "@app/grid/GridCell";
import { isReadOnlyLinkChainColumn } from "@app/grid/linkChain";
import { newRowWrites } from "@app/grid/newRowWrites";
import { stripNewRow } from "@app/grid/recordAnchor";
import { Button } from "@comps/ui/button";
import { TableCell, TableRow } from "@comps/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { DeleteRightOption, EditRightAccess, ObjectRightType, ViewRightType, hasGlobalRightAccess, hasGlobalRightOption } from "@dto/Users";
import { AggregationType, FieldType, TYPE_ITEMS_FIELD_KEY, TYPE_ITEMS_OBJECT_KEY, fieldToOutputType } from "@dto/fields";
import { FieldColumn, Item, ItemKey, Line, MetaLine, NewRecordRequest, Record, SingleFilter } from "@dto/records";
import { Group } from "@dto/views";
import { GRAY_600_COLOR, IsDarkColor, applyBrightness } from "@utils/colors";
import { BASE_URL, PATH_SEPARATOR, SELECT_COLUMN_WIDTH, SOURCE_SEPARATOR } from "@utils/constants";
import { deleteRecordsUndoable, getRecordKeys } from "@utils/data";
import { isInDialog } from "@utils/editGuard";
import { allowedToAddRecord, cleanFilterFromCondition, cleanGroupsFromCondition, getDirectPathFromFiltersAndGroups, getFilterValues } from "@utils/filters";
import { getFieldByPathKeys } from "@utils/model";
import { toastError, toastSuccess, toastSuccessWithLink } from "@utils/toasts";
import { TW_AddRecordButtonIcon, TW_AddRecordTableRowButton, TW_DataTableBorder, useHover } from "@utils/twClasses";
import { post } from "@utils/utils";
import { getUndoSessionId } from "@utils/session";
import { UndoLabelCode, useUndoStore } from "@utils/undo";
import { Check, Plus } from "lucide-react";
import { TFunction } from "i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { translate, translateHtml } from "src/i18n/i18n";
import { useDataStore, useModelStore, useUserStore } from "src/store";


// How long the refresh waits once the row was left for another one: it may move the
// rows (sorted view), and the second click of a double-click must land on the clicked row.
const LEAVE_REFRESH_DELAY_MS = 350;

// What the row created, with all it takes to delete it after the row is gone
interface CreatedRecord {
  workspaceKey: string
  objectKey: string
  recordKeys: string[]
}

function isColumnReadOnly(fieldColumn: FieldColumn): boolean {
  return fieldColumn.field.readOnly === true || !!fieldColumn.field.formula;
}

function removeNewRowLine() {
  const { metaLines, updateMetaLines } = useDataStore.getState();
  if (metaLines.has(NEW_ROW_ID)) {
    const newMetaLines = new Map<string, MetaLine>(metaLines);
    newMetaLines.delete(NEW_ROW_ID);
    updateMetaLines(newMetaLines);
  }
}

// Nothing may stay selected or edited in a row about to close
function clearNewRowSelection() {
  const { focusPos, editingPos, focusMultiple } = useDataStore.getState();
  const remaining = stripNewRow(focusPos);
  if (remaining !== focusPos || editingPos?.rowId === NEW_ROW_ID) {
    focusMultiple(remaining);
  }
}

function refreshIfStillDisplayed(objectKey: string): Promise<void> {
  if (useModelStore.getState().selectedObjectInfos?.object._key !== objectKey) {
    return Promise.resolve();
  }
  return useDataStore.getState().fetchData({ keepFocusOnRecords: true });
}

async function deleteCreatedRecord(record: CreatedRecord, t: TFunction): Promise<boolean> {
  const response = await deleteRecordsUndoable(record.workspaceKey, record.objectKey, record.recordKeys);
  // No response means success
  if (!response) {
    return true;
  }
  if (isCompozzError(response)) {
    const cError = response as CompozzError;
    toastError(cError.i18n ? translate(t, cError.i18n, cError.params) : cError.message);
  } else {
    toastError(t("record.delete.error"));
  }
  return false;
}

// The row was left without validating it: the record stays, which the user may not
// have expected (leaving a row looks like giving it up). The link removes it without
// having to search for it, whatever is displayed by then.
function toastRecordAdded(record: CreatedRecord, canDelete: boolean, t: TFunction) {
  if (!canDelete) {
    toastSuccess(t("data.record.add.toast.added"));
    return;
  }
  // The toast stays clickable for a moment after the click
  let used = false;
  toastSuccessWithLink(t("data.record.add.toast.added"), t("data.record.add.toast.undo"), () => {
    if (used) {
      return;
    }
    used = true;
    void deleteCreatedRecord(record, t).then(() => refreshIfStillDisplayed(record.objectKey));
  });
}


interface AddRecordTableRowProps {
  values: Map<string, any>
  fieldByName: boolean
  className?: string
  style?: React.CSSProperties
  displayText?: boolean
  disabled?: boolean
  tooltip?: string,
  lineHovered?: boolean
  fieldColumns: FieldColumn[]
  pinIndex: number,
  fieldColumnStarts: number[],
  onEditionStart: () => void,
  onEditionEnd: () => void,
}

function AddRecordTableRow({ values, fieldByName, className, style, displayText, disabled = false, tooltip = undefined, lineHovered = undefined, fieldColumns, pinIndex, fieldColumnStarts, onEditionStart, onEditionEnd }: AddRecordTableRowProps) {

  const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
  const selectedView = useModelStore(state => state.selectedView);
  const workspace = useModelStore(state => state.selectedWorkspace);
  const wsColor = useModelStore(state => state.wsColor);
  const bgColor = applyBrightness(wsColor, 25);
  const textColor = IsDarkColor(bgColor) ? "white" : "black";
  const useButtonHover = useHover({ backgroundColor: "white", color: GRAY_600_COLOR, ...style }, { backgroundColor: bgColor, color: textColor, ...style }, lineHovered);
  const objectInfo = useModelStore(state => state.selectedObjectInfos);
  const { t } = useTranslation();
  const [isAddingRecord, setIsAddingRecord] = useState(false);
  const enableRowActions = useDataStore(state => state.enableRowActions);
  const edit = useDataStore(state => state.edit);
  const focus = useDataStore(state => state.focus);
  const setSelectionAnchor = useDataStore(state => state.setSelectionAnchor);
  const [newRecord, setNewRecord] = useState<Record | null>(null);
  const metaLines = useDataStore(state => state.metaLines);
  const updateMetaLines = useDataStore(state => state.updateMetaLines);
  const [metaLine, setMetaLine] = useState<MetaLine | undefined>(undefined);
  const user = useUserStore(state => state.user);
  const userRole = useModelStore(state => state.userRole);

  const insertNewRecord = useDataStore(state => state.insertNewRecord);
  const relationByKeys = useModelStore(state => state.relationByKeys);
  const fieldByKeys = useModelStore(state => state.fieldByKeys);
  const fieldsByPathKeys = getFieldByPathKeys(objectInfo, objectInfo?.fields ?? null, relationByKeys, fieldByKeys);
  const fetchLine = useDataStore(state => state.fetchLine);

  const hasDeleteAccessOnObject = hasGlobalRightOption(user, userRole, ObjectRightType, DeleteRightOption, selectedObjectInfos?.object?._key);
  const hasDeleteAccessOnView = hasGlobalRightOption(user, userRole, ViewRightType, DeleteRightOption, selectedView?._key);
  const hasDeleteAccess = hasDeleteAccessOnObject && hasDeleteAccessOnView;
  const hasEditAccessOnObject = hasGlobalRightAccess(user, userRole, ObjectRightType, EditRightAccess, selectedObjectInfos?.object?._key);
  const hasEditAccessOnView = hasGlobalRightAccess(user, userRole, ViewRightType, EditRightAccess, selectedView?._key);
  const hasEditAccess = hasEditAccessOnObject && hasEditAccessOnView;

  const rowRef = useRef<HTMLTableRowElement>(null);
  // Where the record was created: the row may be closed from another view
  const createdInRef = useRef({ workspaceKey: "", objectKey: "" });
  // A closing in progress (waiting for the saves, deleting...): it must not start twice
  const closingRef = useRef(false);
  const [isClosing, setIsClosing] = useState(false);
  // Text typed in the edited cell, not saved yet
  const typedRef = useRef(false);

  useEffect(() => {
    setMetaLine(metaLines.get(NEW_ROW_ID) ?? undefined);
  }, [metaLines]);

  const directPathFromFiltersAndGroups = getDirectPathFromFiltersAndGroups(selectedView?.filter, selectedView?.groups ?? null);


  const pathCleanningCondition = (path: string) => {
    const paramSrcsParts = path.split(SOURCE_SEPARATOR);
    if (paramSrcsParts.length !== 2) {
      return false;
    }
    const paramPath = paramSrcsParts[1];
    for (const pathKey of directPathFromFiltersAndGroups) {
      if (path === pathKey) {
        return true;
      }
      const srcsParts = pathKey.split(SOURCE_SEPARATOR);
      if (srcsParts.length !== 2) {
        continue;
      }
      const pathPath = srcsParts[1];
      const pathParts = pathPath.split(PATH_SEPARATOR);
      if (pathParts.length !== 2) {
        continue;
      }
      const linkPart = pathParts[0];
      if (paramPath.startsWith(linkPart)) {
        return false;
      }
    }
    return true;
  }

  const filterCleanningCondition = (singleFilter: SingleFilter) => {
    if (singleFilter.value != null && singleFilter.valueKey != null) {
      return false;
    }
    const pathKey = singleFilter.pathKey;
    return pathCleanningCondition(pathKey);
  }

  const groupsCleanningCondition = (group: Group) => {
    const pathKey = group.pathKey;
    if (!pathKey) {
      return false;
    }
    return pathCleanningCondition(pathKey);
  }

  const cleanedFilter = cleanFilterFromCondition(selectedView?.filter ?? null, filterCleanningCondition);
  const cleanedGroups = cleanGroupsFromCondition(selectedView?.groups ?? null, groupsCleanningCondition);

  const doFilterAllowedCreation = allowedToAddRecord(cleanedFilter, fieldsByPathKeys, cleanedGroups, selectedView?.aggregations ?? new Map(), fieldByKeys);

  const isDisabled = disabled || !doFilterAllowedCreation;


  const addRecord = () => {
    if (!workspace || !objectInfo) {
      console.error("No workspace or object selected");
      return;
    }
    const newValues = new Map<string, any>(values);

    let overridenLinkByValue: Map<string, boolean> | undefined;
    if (selectedView?.filter) {
      const filterValues = getFilterValues(cleanedFilter, fieldsByPathKeys, fieldByKeys);
      const values = filterValues.filterValues;
      overridenLinkByValue = filterValues.overridenLinkByValue;
      if (values.size > 0) {
        values.forEach((value, key) => {
          newValues.set(key, value);
        });
      }
    }

    const newRecordRequest: NewRecordRequest = {
      workspaceKey: workspace._key,
      objectKey: objectInfo.object._key,
      fieldValues: newValues,
      fieldByName: fieldByName,
      linkByValue: false,
      overridenLinkByValue: overridenLinkByValue,
      sessionId: getUndoSessionId(),
    }
    post<Record>(`${BASE_URL}/data/records`, newRecordRequest).then(response => {
      if (isCompozzError(response)) {
        const cError = response as CompozzError;
        if (cError.i18n) {
          toastError(translate(t, cError.i18n, cError.params));
        } else {
          toastError(cError.message);
        }
      } else {
        const newRecord = response as Record;
        useUndoStore.getState().markUndoable(objectInfo.object._key, UndoLabelCode.create, 1);
        newRowWrites.watch(newRecord._key);
        createdInRef.current = { workspaceKey: workspace._key, objectKey: objectInfo.object._key };
        typedRef.current = false;
        setIsAddingRecord(true);
        enableRowActions(false);
        onEditionStart();
        setNewRecord(newRecord);
        setSelectionAnchor(null);
        const initialSelection = initialNewRowSelection(fieldColumns.map(fieldColumn => {
          const field = fieldColumn.srcField ?? fieldColumn.field;
          const outputType = fieldToOutputType(field, field.aggregation ?? AggregationType.NO_AGGR, fieldByKeys);
          return {
            readOnly: !hasEditAccess || isColumnReadOnly(fieldColumn) || isReadOnlyLinkChainColumn(fieldColumn, relationByKeys, fieldByKeys),
            noEditor: outputType === FieldType.Boolean || outputType === FieldType.Button,
          };
        }));
        if (initialSelection.edit) {
          edit(initialSelection.position);
        } else {
          focus(initialSelection.position);
        }
        const newLine: Line = {
          items: fieldColumns.map(fieldColumn => {
            let parentKey = null;
            let itemKey = { objectKey: objectInfo.object._key, recordKey: newRecord._key, fieldKey: fieldColumn.field._key } as ItemKey;
            if (fieldColumn.srcField) {
              parentKey = { objectKey: objectInfo.object._key, recordKey: newRecord._key };
              const objectKey = fieldColumn.srcField.typeItems?.get(TYPE_ITEMS_OBJECT_KEY) || "";
              const fieldKey = fieldColumn.srcField.typeItems?.get(TYPE_ITEMS_FIELD_KEY) || "";
              itemKey = { objectKey: objectKey, fieldKey: fieldKey, recordKey: "" };
            }

            const value = (!fieldColumn.srcField && !fieldColumn.isPivot && fieldColumn.field.type !== FieldType.Link)
              ? (newValues.get(fieldColumn.field._key) ?? (fieldByName ? newValues.get(fieldColumn.field.name) : null) ?? null)
              : null;
            return ({
              key: itemKey,
              value: value,
              readOnly: fieldColumn.field.readOnly,
              aggregated: false,
              parentKey: parentKey
            })
          }),
          children: null,
          isLeftGrouped: false
        }
        const newMetaLine: MetaLine = {
          id: NEW_ROW_ID,
          line: newLine,
          parentId: null,
          childrenIds: [],
          level: 0
        }
        // Read from the store: the view may have been refreshed since the click
        const newMetaLines = new Map<string, MetaLine>(useDataStore.getState().metaLines);
        newMetaLines.set(NEW_ROW_ID, newMetaLine);
        updateMetaLines(newMetaLines);
        const recordKeys = getRecordKeys(newMetaLine, objectInfo.object._key);
        if (recordKeys.length > 0) {
          fetchLine(NEW_ROW_ID, recordKeys, selectedView);
        }
      }
    })
  }

  const startClosing = () => {
    closingRef.current = true;
    setIsClosing(true);
  }

  const stopClosing = () => {
    closingRef.current = false;
    setIsClosing(false);
  }

  const closeRow = (recordKey: string) => {
    newRowWrites.unwatch(recordKey);
    stopClosing();
    setIsAddingRecord(false);
    setNewRecord(null);
    enableRowActions(true);
    onEditionEnd();
  }

  const createdRecord = (record: Record): CreatedRecord => {
    const recordKeys = [record._key];
    // Only a pivot view creates other records on this row (one per pivot value).
    // Elsewhere, another record of this object is a linked one (self-relation), which must stay.
    const line = useDataStore.getState().metaLines.get(NEW_ROW_ID);
    if (selectedView?.pivot?.pivotPathKey && line) {
      getRecordKeys(line, createdInRef.current.objectKey).forEach(recordKey => {
        if (!recordKeys.includes(recordKey)) {
          recordKeys.push(recordKey);
        }
      });
    }
    return { ...createdInRef.current, recordKeys };
  }

  // Turns the row into a regular line of the grid, until the refresh puts it in its place
  const insertRecord = async (record: CreatedRecord, recordKey: string, written: boolean) => {
    if (written) {
      // The saves of the cells do not update the line of the row
      await fetchLine(NEW_ROW_ID, record.recordKeys, selectedView).catch(() => { });
    }
    insertNewRecord(NEW_ROW_ID);
    closeRow(recordKey);
  }

  const validateAddingRecord = async () => {
    if (closingRef.current || !newRecord) {
      return;
    }
    startClosing();
    clearNewRowSelection();
    const written = await newRowWrites.settled();
    const record = createdRecord(newRecord);
    await insertRecord(record, newRecord._key, written);
    await refreshIfStillDisplayed(record.objectKey);
  }

  const cancelAddingRecord = async () => {
    if (closingRef.current || !newRecord || !hasDeleteAccess) {
      return;
    }
    startClosing();
    // A save reaching a deleted record would fail
    await newRowWrites.settled();
    const record = createdRecord(newRecord);
    if (await deleteCreatedRecord(record, t)) {
      removeNewRowLine();
      clearNewRowSelection();
      closeRow(newRecord._key);
      await refreshIfStillDisplayed(record.objectKey);
    } else {
      stopClosing();
    }
  }

  // Another row of the grid was selected: what was entered is already saved, so it
  // stays, unless nothing was entered: an empty record is of no use to anyone.
  const leaveRow = async () => {
    if (closingRef.current || !newRecord) {
      return;
    }
    startClosing();
    clearNewRowSelection();
    const written = await newRowWrites.settled();
    const record = createdRecord(newRecord);
    if (written || !hasDeleteAccess) {
      await insertRecord(record, newRecord._key, written);
      toastRecordAdded(record, hasDeleteAccess, t);
    } else {
      removeNewRowLine();
      closeRow(newRecord._key);
      await deleteCreatedRecord(record, t);
    }
    setTimeout(() => refreshIfStillDisplayed(record.objectKey), LEAVE_REFRESH_DELAY_MS);
  }

  // The view or the object changed: same as leaving the row, which is already gone
  const leaveOnUnmount = () => {
    if (!newRecord) {
      return;
    }
    const record = createdRecord(newRecord);
    const recordKey = newRecord._key;
    const canDelete = hasDeleteAccess;
    removeNewRowLine();
    if (closingRef.current) {
      return;
    }
    enableRowActions(true);
    onEditionEnd();
    void newRowWrites.settled().then(async (written) => {
      newRowWrites.unwatch(recordKey);
      if (written || !canDelete) {
        toastRecordAdded(record, canDelete, t);
      } else {
        await deleteCreatedRecord(record, t);
      }
      await refreshIfStillDisplayed(record.objectKey);
    });
  }

  // Two levels: Escape first gives up what is being entered in the cell, and only
  // cancels the row when there is nothing to give up. This is decided on the window,
  // in the capture phase: Radix handles Escape on the document, in the capture phase
  // too, and closing a popover clears the edited cell before any other handler runs.
  const onWindowKeyDown = (event: KeyboardEvent) => {
    const target = event.target;
    if (event.key !== "Escape" || event.repeat || !hasDeleteAccess || !(target instanceof Element)) {
      return;
    }
    const { editingPos, focusPos, editInitialKey } = useDataStore.getState();
    const editingRow = editingPos?.rowId === NEW_ROW_ID;
    const inRow = !!rowRef.current?.contains(target);
    // The editors of a cell (popover, dialog) are rendered out of the row
    const inCellEditor = editingRow && !!target.closest('[role="dialog"], [data-radix-popper-content-wrapper]');
    // The DOM focus falls to the body when the edited input loses it
    const onBody = target === document.body && !!focusPos?.some(position => position.rowId === NEW_ROW_ID) && !isInDialog(target);
    if (!inRow && !inCellEditor && !onBody) {
      return;
    }
    if (closingRef.current) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }
    const cellEdited = editingRow && !onBody;
    if (cellEdited && (newRowWrites.hasInput() || typedRef.current || editInitialKey !== null)) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    focus(null);
    void cancelAddingRecord();
  }

  // The latest version of what the listeners below call
  const latest = useRef({ leaveRow, leaveOnUnmount, onWindowKeyDown });
  latest.current = { leaveRow, leaveOnUnmount, onWindowKeyDown };

  useEffect(() => {
    if (!isAddingRecord) {
      return;
    }
    return useDataStore.subscribe((state, previous) => {
      if (state.editingPos !== previous.editingPos) {
        typedRef.current = false;
      }
      const leftFocus = state.focusPos !== previous.focusPos && !!state.focusPos?.some(position => position.rowId !== NEW_ROW_ID);
      // An emptied selection is no choice of the user (view reloaded...)
      const tickedRow = state.rowSelection !== previous.rowSelection && Object.keys(state.rowSelection).length > 0;
      if (leftFocus || tickedRow) {
        // The selection of the clicked cell is still being updated
        queueMicrotask(() => { void latest.current.leaveRow(); });
      }
    });
  }, [isAddingRecord]);

  useEffect(() => {
    if (!isAddingRecord) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => latest.current.onWindowKeyDown(event);
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [isAddingRecord]);

  useEffect(() => () => latest.current.leaveOnUnmount(), []);

  // Within the row, a cell is only selected: no multiple selection there. Stable, as
  // the cells do not render again for a change of this callback.
  const selectCell = useCallback((position: CellPosition) => {
    useDataStore.getState().focus(position);
  }, []);

  const addingLeftCellStyle = {
    borderTop: "2px solid " + wsColor,
    borderBottom: "2px solid " + wsColor,
    borderLeft: "2px solid " + wsColor,
    borderRight: "1px solid " + "var(--TABLE_BORDER)",
    backgroundColor: "white",
    borderRadius: "0.25rem 0 0 0.25rem",
    position: "sticky",
    left: "0",
    zIndex: 15
  } as React.CSSProperties;

  const addingRightCellStyle = {
    borderTop: "2px solid " + wsColor,
    borderBottom: "2px solid " + wsColor,
    borderLeft: "1px solid " + "var(--TABLE_BORDER)",
    borderRight: "2px solid " + wsColor,
    backgroundColor: "white",
    borderRadius: "0 0.25rem 0.25rem 0",
  }

  const addingCellStyle = {
    borderTop: "2px solid " + wsColor,
    borderBottom: "2px solid " + wsColor,
    borderLeft: "1px solid " + "var(--TABLE_BORDER)",
    borderRight: "1px solid " + "var(--TABLE_BORDER)",
    backgroundColor: "white",
  }

  function getColumnStyle(curIndex: number, pinIndex: number, nbColumns: number): React.CSSProperties {
    const isPinned = curIndex <= pinIndex;
    const left = fieldColumnStarts[curIndex] ?? SELECT_COLUMN_WIDTH;
    let zIndex = 5;
    if (isPinned) {
      zIndex = 7;
    }
    let borderStyle = (curIndex < nbColumns - 1) ? addingCellStyle : addingRightCellStyle
    if (curIndex === pinIndex) {
      borderStyle = {
        ...borderStyle,
        borderRight: "1px solid " + GRAY_600_COLOR,
      }
    }
    return {
      position: isPinned ? 'sticky' : 'relative',
      left: isPinned ? `${left}px` : undefined,
      zIndex: zIndex,
      ...borderStyle
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (!isAddingRecord || closingRef.current) {
      return;
    }
    if (event.key === "Enter" && event.ctrlKey) {
      const validating = validateAddingRecord();
      if (event.shiftKey) {
        void validating.then(() => addRecord());
      }
    }
  }

  const addRecordButton = (
    <>
      {(isDisabled || tooltip) && (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger style={style}>
              <Button className={TW_AddRecordTableRowButton + (className ? " " + className : "")} onClick={addRecord} disabled={isDisabled} {...useButtonHover}>
                <Plus className={TW_AddRecordButtonIcon + (useButtonHover.isHovered ? " text-black" : " text-gray-400")} />{displayText && t("data.record.add.button")}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {tooltip ? tooltip : t("data.record.add.button.disabled")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
      }
      {!isDisabled && !tooltip && (
        <Button className={TW_AddRecordTableRowButton + (className ? " " + className : "")} onClick={addRecord} disabled={isDisabled} {...useButtonHover}>
          <Plus className={TW_AddRecordButtonIcon + (useButtonHover.isHovered ? " text-black" : " text-gray-400")} />{displayText && t("data.record.add.button")}
        </Button>
      )}
    </>
  )


  return (
    <TableRow ref={rowRef} className={"sticky bottom-0 bg-transparent hover:bg-transparent h-12" + (isClosing ? " pointer-events-none" : "")} style={{ zIndex: 20 }} onKeyDown={handleKeyDown} onInputCapture={() => { typedRef.current = true; }}>
      {(!isAddingRecord) &&
        <TableCell className="sticky left-2">
          {addRecordButton}
        </TableCell>
      }
      {(isAddingRecord) &&
        <>

          <TableCell colSpan={1} style={addingLeftCellStyle} className="p-0">
          <div className="absolute -top-8 left-0 flex flex-row justify-start items-center gap-1">
            <TooltipProvider delayDuration={1000}>
              <Tooltip>
                <TooltipTrigger asChild>
                    <div className="bg-white rounded-lg p-1 cursor-pointer border-l-1 border-t-1 border-r-1 border-b-1" style={{ borderColor: wsColor }} onClick={validateAddingRecord}>
                      <p className="text-xs text-left font-bold" style={{ color: wsColor }}>{t("data.record.add.validate.button")}</p>
                    </div>
                </TooltipTrigger>
                <TooltipContent>
                  {translateHtml(t, "data.record.add.validate.tooltip")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {hasDeleteAccess && (
              <TooltipProvider delayDuration={1000}>
              <Tooltip>
                <TooltipTrigger asChild>
                    <div className="bg-white rounded-lg p-1 cursor-pointer border-l-1 border-t-1 border-r-1 border-b-1" style={{ borderColor: wsColor }} onClick={cancelAddingRecord}>
                      <p className="text-xs text-left font-bold" style={{ color: wsColor }}>{t("data.record.add.cancel.button")}</p>
                    </div>
                </TooltipTrigger>
                <TooltipContent>
                  {translateHtml(t, "data.record.add.cancel.tooltip")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            )}
            </div>
            <div className="flex flex-col justify-start items-center gap-0.5">
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger>
                    <Check className="cursor-pointer h-4 w-4" style={{ color: wsColor }} onClick={validateAddingRecord} />
                  </TooltipTrigger>
                  <TooltipContent>
                    {translateHtml(t, "data.record.add.validate.tooltip")}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </TableCell>
          {fieldColumns.map((fieldColumn, index) => {
            let borderClass = TW_DataTableBorder;
            borderClass += " border-l-0 ";
            borderClass += " overflow-hidden text-ellipsis text-nowrap whitespace-nowrap p-0";

            const position = { rowId: NEW_ROW_ID, column: index };
            let itemKey = fieldColumn.isPivot ? { objectKey: objectInfo?.object._key || "", fieldKey: fieldColumn.field._key } as ItemKey : { objectKey: objectInfo?.object._key || "", fieldKey: fieldColumn.field._key, recordKey: newRecord?._key || "" } as ItemKey
            let parentKey = null;
            const srcField = fieldColumn.srcField;
            if (srcField && srcField.type === FieldType.Link) {
              parentKey = { objectKey: objectInfo?.object._key || "", recordKey: newRecord?._key || "" };
              const objectKey = srcField.typeItems?.get(TYPE_ITEMS_OBJECT_KEY) || "";
              const fieldKey = srcField.typeItems?.get(TYPE_ITEMS_FIELD_KEY) || "";
              const recordKey = "";
              itemKey = { objectKey: objectKey, fieldKey: fieldKey, recordKey: recordKey };
            }
            let item: Item = { key: itemKey as ItemKey, value: null, readOnly: isColumnReadOnly(fieldColumn), aggregated: false, parentKey: parentKey };
            if (metaLine && metaLine.line.items.length > 0) {
              item = metaLine.line.items[index];
            }
            return (
              <TableCell key={"adding-record-table-row-cell-" + fieldColumn.field._key + "-" + index} className={borderClass} style={getColumnStyle(index, pinIndex, fieldColumns.length)} >
                <GridCell
                  key={"grid-cell-new-record-" + fieldColumn.field._key + "-" + index}
                  fieldColumn={fieldColumn}
                  item={item}
                  position={position}
                  noDataFetch={true}
                  onSelectionChange={selectCell}
                />
              </TableCell>
            )
          })}
        </>
      }
    </TableRow>
  )


}

export default AddRecordTableRow;

